import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { designerCanvasStore } from "../../views/Board/Workspaces/EditWorkspace/Designer/stores/designerCanvasStore.ts";

export const ZoomableComponent = ({
  zoomableComponentId,
  zoomableComponentRealSize,
  children,
}: {
  zoomableComponentId: string;
  zoomableComponentRealSize: { width: number; height: number };
  children: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const zoomLevel = designerCanvasStore.useState((state) => state.zoomLevel);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });

  // XXX: To be responsive to window resize, we need to listen for container dimension changes and update the container dimensions accordingly,
  // so all the zoom calculations stay consistent. Using only references, will not rerender the component, and though lead to visual glitches.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerDimensions({ width, height });
      }
    });
    resizeObserver.observe(element);
    setContainerDimensions({
      width: element.clientWidth,
      height: element.clientHeight,
    });
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // As this constant is used as dependency of another hook, we are forced to use `useMemo` even if computation is trivial.
  const scaledSize = useMemo(
    () => ({
      width: zoomableComponentRealSize.width * zoomLevel,
      height: zoomableComponentRealSize.height * zoomLevel,
    }),
    [zoomableComponentRealSize, zoomLevel],
  );

  const shouldCenterXInViewport = scaledSize.width < containerDimensions.width;
  const shouldCenterYInViewport =
    scaledSize.height < containerDimensions.height;

  const previousContentCenter = useRef<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const isInitialized = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleScroll = () => {
      previousContentCenter.current = {
        x: (element.scrollLeft + containerDimensions.width / 2) / zoomLevel,
        y: (element.scrollTop + containerDimensions.height / 2) / zoomLevel,
      };
    };

    element.addEventListener("scroll", handleScroll, { passive: true });
    return () => element.removeEventListener("scroll", handleScroll);
  }, [containerDimensions, zoomLevel]);

  useEffect(() => {
    const element = ref.current;
    if (
      !element ||
      (containerDimensions.width === 0 && containerDimensions.height === 0)
    ) {
      return;
    }

    // Initialize content center on first render or when transitioning from fit-to-viewport to scrollable
    if (
      !isInitialized.current ||
      (shouldCenterXInViewport && shouldCenterYInViewport)
    ) {
      previousContentCenter.current = {
        x: zoomableComponentRealSize.width / 2,
        y: zoomableComponentRealSize.height / 2,
      };
      isInitialized.current = true;
    }

    // Handle X axis scrolling f shouldCenterXInViewport is true, targetScrollLeft stays 0 (centered via CSS)
    let targetScrollLeft = 0;
    if (!shouldCenterXInViewport) {
      const targetContentX = previousContentCenter.current.x;
      const calculatedScrollLeft =
        targetContentX * zoomLevel - containerDimensions.width / 2;
      const maxScrollLeft = Math.max(
        0,
        scaledSize.width - containerDimensions.width,
      );
      targetScrollLeft = Math.max(
        0,
        Math.min(maxScrollLeft, calculatedScrollLeft),
      );
    }

    // Handle Y axis scrolling, if shouldCenterYInViewport is true, targetScrollTop stays 0 (centered via CSS)
    let targetScrollTop = 0;
    if (!shouldCenterYInViewport) {
      // Y axis needs scrolling - preserve content center
      const targetContentY = previousContentCenter.current.y;
      const calculatedScrollTop =
        targetContentY * zoomLevel - containerDimensions.height / 2;
      const maxScrollTop = Math.max(
        0,
        scaledSize.height - containerDimensions.height,
      );
      targetScrollTop = Math.max(
        0,
        Math.min(maxScrollTop, calculatedScrollTop),
      );
    }

    element.scrollTo({ top: targetScrollTop, left: targetScrollLeft });

    previousContentCenter.current = {
      x: (targetScrollLeft + containerDimensions.width / 2) / zoomLevel,
      y: (targetScrollTop + containerDimensions.height / 2) / zoomLevel,
    };
  }, [
    scaledSize,
    containerDimensions,
    shouldCenterXInViewport,
    shouldCenterYInViewport,
    zoomLevel,
    zoomableComponentRealSize,
  ]);

  return (
    <div
      id={zoomableComponentId}
      ref={ref}
      className="relative w-full h-full overflow-auto"
    >
      <div
        className={classNames(
          "absolute",
          shouldCenterXInViewport && "left-1/2",
          shouldCenterYInViewport && "top-1/2",
        )}
        style={{
          ...(!shouldCenterXInViewport && {
            left: `${String(scaledSize.width / 2)}px`,
          }),
          ...(!shouldCenterYInViewport && {
            top: `${String(scaledSize.height / 2)}px`,
          }),
          transform: `translate(-50%, -50%) scale(${String(zoomLevel)})`,
          transformOrigin: "center",
          width: `${String(zoomableComponentRealSize.width)}px`,
          height: `${String(zoomableComponentRealSize.height)}px`,
        }}
      >
        {children}
      </div>
    </div>
  );
};
